// src/pages/Admin/MembersDashboard.js
import { useEffect, useMemo, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
  setDoc,
  runTransaction,
  getDoc,
} from "firebase/firestore";
import { db } from "../../firebaseConfig";
import AdminGate from "../../components/AdminGate";
import { generateApplicationForm } from "../../pdfs/generateApplicationForm";

/* ---------------- helpers ---------------- */
function formatDate(ts) {
  try {
    if (!ts) return "—";
    if (typeof ts.toDate === "function") return ts.toDate().toLocaleString();
    if (ts instanceof Date) return ts.toLocaleString();
    if (typeof ts === "number") {
      const t = ts > 1e12 ? ts : ts * 1000;
      return new Date(t).toLocaleString();
    }
    const d = new Date(ts);
    return isNaN(d.getTime()) ? "—" : d.toLocaleString();
  } catch {
    return "—";
  }
}
function clsx(...xs) { return xs.filter(Boolean).join(" "); }
function downloadCSV(filename, rows) {
  if (!rows?.length) return;
  const keys = new Set();
  rows.forEach((r) => Object.keys(r || {}).forEach((k) => keys.add(k)));
  const headers = Array.from(keys);
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""').replace(/\r?\n/g, " ") }"`;
  const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => esc(r[h])).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
}



const StatusPill = ({ status }) => {
  const s = String(status || "pending").toLowerCase();
  const map = {
    approved: "bg-emerald-100 text-emerald-800 border-emerald-300",
    rejected: "bg-rose-100 text-rose-800 border-rose-300",
    pending: "bg-amber-100 text-amber-800 border-amber-300",
  };
  const label = s === "approved" ? "APPROVED" : s === "rejected" ? "REJECTED" : "PENDING";
  return (
    <span className={clsx("inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold border", map[s] || map.pending)}>
      {label}
    </span>
  );
};

/* ---------- MemberID smart comparator ---------- */
function parseMemberId(x) {
  if (!x) return null;
  const m = String(x).match(/^([A-Za-z]+)-(\d{4})-(\d+)$/);
  if (!m) return null;
  return { prefix: m[1].toUpperCase(), year: Number(m[2]), seq: Number(m[3]) };
}
function compareMemberId(aId, bId) {
  const a = parseMemberId(aId);
  const b = parseMemberId(bId);
  if (a && b) {
    if (a.prefix !== b.prefix) return a.prefix < b.prefix ? -1 : 1;
    if (a.year !== b.year) return a.year - b.year;
    return a.seq - b.seq;
  }
  return String(aId || "").localeCompare(String(bId || ""));
}

/* ---------- date helpers for reports ---------- */
function toMs(x) {
  try {
    if (!x) return null;
    if (typeof x?.toDate === "function") return x.toDate().getTime();
    if (x instanceof Date) return x.getTime();
    const d = new Date(x);
    const t = d.getTime();
    return isNaN(t) ? null : t;
  } catch {
    return null;
  }
}

/* ✅ FULL FLATTEN for Excel/CSV (ALL DETAILS + nested documents.*) */
function flattenAny(obj, prefix = "", out = {}) {
  if (obj == null) {
    out[prefix || "value"] = "";
    return out;
  }
  if (typeof obj?.toDate === "function") {
    out[prefix] = obj.toDate().toISOString();
    return out;
  }
  if (obj instanceof Date) {
    out[prefix] = obj.toISOString();
    return out;
  }
  if (Array.isArray(obj)) {
    out[prefix] = obj
      .map((v) => (v?.toDate ? v.toDate().toISOString() : String(v ?? "")))
      .join(" | ");
    return out;
  }
  if (typeof obj !== "object") {
    out[prefix] = String(obj);
    return out;
  }
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    const key = prefix ? `${prefix}.${k}` : k;
    if (v == null) out[key] = "";
    else if (typeof v?.toDate === "function") out[key] = v.toDate().toISOString();
    else if (v instanceof Date) out[key] = v.toISOString();
    else if (Array.isArray(v)) out[key] = v.map((x) => (x?.toDate ? x.toDate().toISOString() : String(x ?? ""))).join(" | ");
    else if (typeof v === "object") flattenAny(v, key, out);
    else out[key] = String(v);
  });
  return out;
}

/* ✅ optional: Cloud Function email trigger (safe call) */
async function triggerEmail({ action, memberDocId, sourceCollection }) {
  try {
    const mod = await import("firebase/functions");
    const functions = mod.getFunctions(undefined, "us-central1"); // ✅ FIX: region set
    const sendDecisionEmail = mod.httpsCallable(functions, "sendMembershipDecisionEmail");
    await sendDecisionEmail({ action, memberDocId, sourceCollection });
  } catch (e) {
    console.error("[email trigger] failed (optional):", e?.message || e);
  }
}

/* ===================== main page ===================== */
export default function MembersDashboard() {
  const [members, setMembers] = useState([]);
  const [source, setSource] = useState("members"); // detected source
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // filters
  const [qText, setQText] = useState("");
  const [statusFilter, setStatusFilter] = useState(""); // pending/approved/rejected
  const [stateFilter, setStateFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [talukaFilter, setTalukaFilter] = useState("");
  const [villageFilter, setVillageFilter] = useState("");
  const [cropFilter, setCropFilter] = useState(""); // single crop for quick filter
  const [typeFilter, setTypeFilter] = useState(""); // REGULAR/NOMINAL
  const [categoryFilter, setCategoryFilter] = useState(""); // Farmer/FPO/SHG/Cooperative Society
  const [fromDate, setFromDate] = useState(""); // yyyy-mm-dd
  const [toDate, setToDate] = useState("");   // yyyy-mm-dd

  // sorting (default Member ID A→Z)
  const [sortKey, setSortKey] = useState("memberId:asc");

  // pagination
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  // modals
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState({});
  const [viewing, setViewing] = useState(null);

  // --- resilient subscription with fallbacks ---
  useEffect(() => {
  let unsub = null;
  let triedFallback = false;
  let triedSecond = false;

  const subscribe = (colName) => {
    setSource(colName);
    return onSnapshot(
      query(collection(db, colName)),
      (snap) => {
        const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        if (arr.length === 0 && colName === "members" && !triedFallback) {
          triedFallback = true;
          if (unsub) unsub();
          unsub = subscribe("Members");
          return;
        }
        if (arr.length === 0 && colName === "Members" && !triedSecond) {
          triedSecond = true;
          if (unsub) unsub();
          unsub = subscribe("memberRegister");
          return;
        }
        setMembers(arr);
        setLoading(false);
        setError("");
      },
      (err) => {
        console.error(`[members] subscribe error (${colName})`, err);
        if (colName === "members" && !triedFallback) {
          triedFallback = true;
          if (unsub) unsub();
          unsub = subscribe("Members");
        } else if (colName === "Members" && !triedSecond) {
          triedSecond = true;
          if (unsub) unsub();
          unsub = subscribe("memberRegister");
        } else {
          setError("Failed to load members.");
          setLoading(false);
        }
      }
    );
  };

  unsub = subscribe("members");
  return () => unsub && unsub();
}, []);
  const counts = useMemo(() => {
    const c = { total: members.length, pending: 0, approved: 0, rejected: 0 };
    members.forEach(m => {
      const s = String(m.status || "pending").toLowerCase();
      if (s === "approved") c.approved++;
      else if (s === "rejected") c.rejected++;
      else c.pending++;
    });
    return c;
  }, [members]);

  // dropdown option lists from data
  const options = useMemo(() => {
    const states = new Set();
    const districts = new Set();
    const talukas = new Set();
    const villages = new Set();
    const crops = new Set();
    const types = new Set();
    const cats = new Set();

    members.forEach((m) => {
      if (m.state) states.add(String(m.state));
      if (m.district) districts.add(String(m.district));
      if (m.taluka) talukas.add(String(m.taluka));
      if (m.village) villages.add(String(m.village));
      if (m.membership_type) types.add(String(m.membership_type).toUpperCase());
      if (m.farmer_category) cats.add(String(m.farmer_category));

      const c = m.crops;
      if (Array.isArray(c)) c.forEach((x) => x && crops.add(String(x)));
      else if (typeof c === "string" && c.trim()) crops.add(c.trim());
    });

    const sortA = (a, b) => String(a).localeCompare(String(b));

    // ✅ ALWAYS show REGULAR/NOMINAL in filter
    const fixedTypes = ["REGULAR", "NOMINAL"];
    const mergedTypes = Array.from(new Set([...fixedTypes, ...Array.from(types)])).sort(sortA);

    return {
      states: Array.from(states).sort(sortA),
      districts: Array.from(districts).sort(sortA),
      talukas: Array.from(talukas).sort(sortA),
      villages: Array.from(villages).sort(sortA),
      crops: Array.from(crops).sort(sortA),
      types: mergedTypes,
      categories: Array.from(cats).sort(sortA),
    };
  }, [members]);

  const filtered = useMemo(() => {
    const t = qText.trim().toLowerCase();
    const fromMs = fromDate ? new Date(fromDate + "T00:00:00").getTime() : null;
    const toMsX = toDate ? new Date(toDate + "T23:59:59").getTime() : null;

    let rows = members.filter((m) => {
      const s = String(m.status || "pending").toLowerCase();
      if (statusFilter && s !== statusFilter) return false;

      if (stateFilter && String(m.state || "") !== stateFilter) return false;
      if (districtFilter && String(m.district || "") !== districtFilter) return false;
      if (talukaFilter && String(m.taluka || "") !== talukaFilter) return false;
      if (villageFilter && String(m.village || "") !== villageFilter) return false;

      if (typeFilter && String(m.membership_type || "").toUpperCase() !== typeFilter) return false;
      if (categoryFilter && String(m.farmer_category || "") !== categoryFilter) return false;

      if (cropFilter) {
        const c = m.crops;
        const ok = Array.isArray(c) ? c.includes(cropFilter) : String(c || "") === cropFilter;
        if (!ok) return false;
      }

      const created = m.createdAt || m.created_at || m.created || null;
      const cMs = toMs(created);
      if (fromMs && cMs && cMs < fromMs) return false;
      if (toMsX && cMs && cMs > toMsX) return false;
      if ((fromMs || toMsX) && !cMs) return false;

      if (!t) return true;
      const hay = `${m.name || ""} ${m.member_id || ""} ${m.final_member_id || ""} ${m.email || ""} ${m.mobile || ""} ${m.state || ""} ${m.district || ""} ${m.taluka || ""} ${m.village || ""}`.toLowerCase();
      return hay.includes(t);
    });

    const [k, dir = "asc"] = sortKey.split(":");
    const mul = dir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      if (k === "memberId") {
        const aId = a.member_id || a.final_member_id || "APPLIED";
        const bId = b.member_id || b.final_member_id || "APPLIED";
        return compareMemberId(aId, bId) * mul;
      }
      if (k === "name") return (String(a.name || "").localeCompare(String(b.name || ""))) * mul;
      if (k === "createdAt") {
        const aa = toMs(a.createdAt || a.created_at || a.created) || 0;
        const bb = toMs(b.createdAt || b.created_at || b.created) || 0;
        return (aa - bb) * mul;
      }
      return 0;
    });

    return rows;
  }, [
    members,
    qText,
    statusFilter,
    sortKey,
    stateFilter,
    districtFilter,
    talukaFilter,
    villageFilter,
    cropFilter,
    typeFilter,
    categoryFilter,
    fromDate,
    toDate,
  ]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const pageData = filtered.slice((page - 1) * pageSize, page * pageSize);

  /* ---------------- actions ---------------- */

  // ✅ Approve + Membership ID (REGULAR: SAAM-2025-0001, NOMINAL: SAAM/NOMINAL/MEMBER/0001)
  // ✅ works for Pending OR Rejected
  async function approveMember(memberId) {
    let finalId = null;
    let finalStatusWas = null;

    try {
      await runTransaction(db, async (tx) => {
        const ref = doc(db, source, memberId);
        const snap = await tx.get(ref);
        if (!snap.exists()) throw new Error("Member not found");

        const m = snap.data() || {};
        finalStatusWas = String(m.status || "pending").toLowerCase();

        // already approved -> keep id
        if (finalStatusWas === "approved" && m.member_id) {
          finalId = m.member_id;
          return;
        }

        // robust type detection
        const typeRaw = m.membership_type || m.membershipType || m.type || m.member_type || "";
        const type = String(typeRaw).toUpperCase();
        const isNominal = type === "NOMINAL";

        if (isNominal) {
          const nomRef = doc(db, "membership_counters", "nominal");
          const nSnap = await tx.get(nomRef);
          const next = (nSnap.exists() ? (nSnap.data().seq || 0) : 0) + 1;
          tx.set(nomRef, { seq: next, updatedAt: serverTimestamp() }, { merge: true });
          finalId = `SAAM/NOMINAL/MEMBER/${String(next).padStart(4, "0")}`;
        } else {
          const countersRef = doc(db, "meta", "counters");
          const cSnap = await tx.get(countersRef);
          const next = (cSnap.exists() ? (cSnap.data().memberSeq2025 || 0) : 0) + 1;
          tx.set(countersRef, { memberSeq2025: next }, { merge: true });
          finalId = `SAAM-2025-${String(next).padStart(4, "0")}`;
        }

        tx.update(ref, {
          status: "approved",
          member_id: finalId,
          approvedAt: serverTimestamp(),
        });

        tx.set(doc(collection(db, source, memberId, "audits")), {
          action: "status:approved",
          member_id: finalId,
          prevStatus: finalStatusWas,
          at: serverTimestamp(),
        });
      });

      // ✅ PDF download after approve
      try {
        const snap2 = await getDoc(doc(db, source, memberId));
        if (snap2.exists()) {
          const data = snap2.data();
          generateApplicationForm({
            ...data,
            member_id: finalId || data.member_id,
            application_no: data.application_no,
          });
        }
      } catch (e) {
        console.error("[approveMember] pdf download failed", e);
      }

      // ✅ email trigger (optional)
      await triggerEmail({ action: "APPROVED", memberDocId: memberId, sourceCollection: source });

      alert(`✅ Approved successfully: ${finalId || ""}\nPDF downloaded.\nEmail triggered (if function set).`);
    } catch (e) {
      console.error("[approveMember] error", e?.code, e?.message, e);
      alert(`Approve failed: ${e?.code || ""} ${e?.message || ""}`);
    }
  }

  // ✅ Reject (visible for Pending + Approved)
  async function rejectMember(memberId) {
    try {
      const ref = doc(db, source, memberId);
      await updateDoc(ref, { status: "rejected", rejectedAt: serverTimestamp() });
      await setDoc(doc(collection(db, source, memberId, "audits")), {
        action: "status:rejected",
        at: serverTimestamp(),
      });

      // ✅ email trigger (optional)
      await triggerEmail({ action: "REJECTED", memberDocId: memberId, sourceCollection: source });

      alert("✅ Rejected.");
    } catch (e) {
      console.error("[rejectMember] error", e);
      alert("Reject failed.");
    }
  }

  async function removeMember(memberId) {
    if (!confirm("Delete this member permanently?")) return;
    try {
      // ✅ email trigger (optional)
      await triggerEmail({ action: "DELETED", memberDocId: memberId, sourceCollection: source });

      await deleteDoc(doc(db, source, memberId));
      alert("✅ Deleted.");
    } catch (e) {
      console.error("[deleteMember] error", e);
      alert("Delete failed.");
    }
  }

  // ✅ FULL CSV (ALL fields)
  function exportCSVFull() {
    const rows = filtered.map((m) => flattenAny(m));
    downloadCSV(`members_full_${new Date().toISOString().slice(0, 10)}.csv`, rows);
  }

  // ✅ FULL Excel (ALL fields)
  async function exportExcelFull() {
    try {
      const XLSX = await import("xlsx");
      const rows = filtered.map((m) => flattenAny(m));

      const keys = new Set();
      rows.forEach((r) => Object.keys(r).forEach((k) => keys.add(k)));
      const headers = Array.from(keys);

      const normalized = rows.map((r) => {
        const o = {};
        headers.forEach((h) => (o[h] = r[h] ?? ""));
        return o;
      });

      const ws = XLSX.utils.json_to_sheet(normalized);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "MembersFullReport");
      XLSX.writeFile(wb, `members_full_${new Date().toISOString().slice(0, 10)}.xlsx`);
    } catch (e) {
      console.error("[exportExcelFull] error", e);
      alert("Excel export failed. Please install xlsx: npm i xlsx");
    }
  }

  function clearReportFilters() {
    setStateFilter("");
    setDistrictFilter("");
    setTalukaFilter("");
    setVillageFilter("");
    setCropFilter("");
    setTypeFilter("");
    setCategoryFilter("");
    setFromDate("");
    setToDate("");
    setQText("");
    setStatusFilter("");
    setPage(1);
  }

  /* ---------------- render ---------------- */
  return (
    <AdminGate>
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="max-w-7xl mx-auto px-4 py-6">

          {/* Logout Bar */}
          <div className="flex justify-end mb-4">
            <button
              onClick={() => {
                import("../../services/adminAuth").then(({ adminLogout }) => {
                  adminLogout();
                  window.location.href = "/admin-login";
                });
              }}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              Logout
            </button>
          </div>

          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-4">
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">
                Members Register (Admin)
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                Full automation: Approve/Reject/Delete + Full Excel/CSV (ALL fields) + Nominal series.
              </p>
              <div className="text-xs text-slate-500 mt-1">Data Source: <b>{source}</b></div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <button onClick={exportExcelFull} className="px-4 py-2 rounded-lg border border-black bg-white text-black hover:bg-slate-50">
                Excel (Full)
              </button>
              <button onClick={exportCSVFull} className="px-4 py-2 rounded-lg border border-black bg-white text-black hover:bg-slate-50">
                CSV (Full)
              </button>
              <button onClick={() => window.location.reload()} className="px-4 py-2 rounded-lg border border-black bg-white text-black hover:bg-slate-50">
                Refresh
              </button>
            </div>
          </div>

          {/* Quick counters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
            <div className="bg-white border border-black rounded-lg px-3 py-2 text-sm">Total: <b>{counts.total}</b></div>
            <div className="bg-white border border-black rounded-lg px-3 py-2 text-sm">Pending: <b>{counts.pending}</b></div>
            <div className="bg-white border border-black rounded-lg px-3 py-2 text-sm">Approved: <b>{counts.approved}</b></div>
            <div className="bg-white border border-black rounded-lg px-3 py-2 text-sm">Rejected: <b>{counts.rejected}</b></div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-lg border border-black p-4 mb-5">
            <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
              <div className="md:col-span-2">
                <label className="block text-[11px] font-bold text-slate-700 mb-1">Search</label>
                <input
                  value={qText}
                  onChange={(e) => { setQText(e.target.value); setPage(1); }}
                  placeholder="Name / Member ID / Mobile / Email…"
                  className="w-full border border-black rounded-md px-3 py-2 text-sm focus:ring-0 focus:outline-none"
                />
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 mb-1">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                  className="w-full border border-black rounded-md px-2 py-2 text-sm bg-white"
                >
                  <option value="">All</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-700 mb-1">Type</label>
                <select
                  value={typeFilter}
                  onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
                  className="w-full border border-black rounded-md px-2 py-2 text-sm bg-white"
                >
                  <option value="">All</option>
                  {options.types.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>

              <div className="md:col-span-3 flex items-end gap-2">
                <button
                  onClick={clearReportFilters}
                  className="w-full px-3 py-2 border border-black rounded-md text-sm bg-white hover:bg-slate-50"
                  type="button"
                >
                  Clear Filters
                </button>
              </div>
            </div>

            <div className="mt-3 text-sm text-slate-700">
              Showing <b>{filtered.length}</b> record(s)
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="mb-4 rounded-lg border border-black bg-white text-slate-800 px-4 py-3">
              {error}
            </div>
          )}

          {/* Table */}
          <div className="bg-white rounded-lg border border-black overflow-hidden">
            <div className="w-full overflow-x-auto">
              <table className="w-full text-[13px] border-collapse">
                <thead className="bg-white">
                  <tr className="border-b border-black">
                    <th className="p-2 text-left border-r border-black">Member ID</th>
                    <th className="p-2 text-left border-r border-black">Name</th>
                    <th className="p-2 text-left border-r border-black">Mobile / Email</th>
                    <th className="p-2 text-left border-r border-black">Type</th>
                    <th className="p-2 text-left border-r border-black">Status</th>
                    <th className="p-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr><td colSpan={6} className="p-6 text-center text-slate-600">Loading members…</td></tr>
                  ) : pageData.length === 0 ? (
                    <tr><td colSpan={6} className="p-6 text-center text-slate-600">No members found.</td></tr>
                  ) : (
                    pageData.map((m) => {
                      const mid = m.member_id || m.final_member_id || "APPLIED";
                      const status = String(m.status || "pending").toLowerCase();
                      const type = String(m.membership_type || "").toUpperCase() || "—";

                      return (
                        <tr key={m.id} className="border-b border-black">
                          <td className="p-2 font-bold border-r border-black">{mid}</td>
                          <td className="p-2 border-r border-black">
                            <div className="font-semibold">{m.name || "—"}</div>
                            <div className="text-[11px] text-slate-600">{m.father_name || m.fatherName || "—"}</div>
                          </td>
                          <td className="p-2 border-r border-black">
                            <div className="text-slate-900">{m.mobile || "—"}</div>
                            <div className="text-[11px] text-slate-600">{m.email || "—"}</div>
                          </td>
                          <td className="p-2 border-r border-black">{type}</td>
                          <td className="p-2 border-r border-black"><StatusPill status={status} /></td>
                          <td className="p-2">
                            <div className="flex flex-wrap gap-2">
                              {/* ✅ Approve for Pending + Rejected */}
                              {(status === "pending" || status === "rejected") && (
                                <button onClick={() => approveMember(m.id)} className="px-3 py-1 border border-black bg-white text-black text-xs font-bold hover:bg-slate-50">
                                  APPROVE
                                </button>
                              )}

                              {/* ✅ Reject for Pending + Approved */}
                              {(status === "pending" || status === "approved") && (
                                <button onClick={() => rejectMember(m.id)} className="px-3 py-1 border border-black bg-white text-black text-xs font-bold hover:bg-slate-50">
                                  REJECT
                                </button>
                              )}

                              <button onClick={() => removeMember(m.id)} className="px-3 py-1 border border-black bg-white text-black text-xs font-bold hover:bg-slate-50">
                                DELETE
                              </button>

                              <button onClick={() => generateApplicationForm({ ...m, application_no: m.application_no })} className="px-3 py-1 border border-black bg-white text-black text-xs font-bold hover:bg-slate-50">
                                PDF
                              </button>

                              <button onClick={() => { setViewing(m); setEditing(null); }} className="px-3 py-1 border border-black bg-white text-black text-xs font-bold hover:bg-slate-50">
                                VIEW
                              </button>

                              <button onClick={() => { setEditing(m); setEditData(m); setViewing(null); }} className="px-3 py-1 border border-black bg-white text-black text-xs font-bold hover:bg-slate-50">
                                EDIT
                              </button>
                            </div>

                            <div className="mt-1 text-[10px] text-slate-500">
                              Created: {formatDate(m.createdAt || m.created_at)}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between p-3 border-t border-black bg-white">
              <div className="text-[12px] text-slate-700">
                Showing {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, filtered.length)} of {filtered.length}
              </div>
              <div className="flex items-center gap-2">
                <button className="px-3 py-1 border border-black rounded text-sm hover:bg-slate-50 disabled:opacity-50" onClick={() => setPage(1)} disabled={page === 1}>First</button>
                <button className="px-3 py-1 border border-black rounded text-sm hover:bg-slate-50 disabled:opacity-50" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>Prev</button>
                <div className="px-3 py-1 text-sm text-slate-800">{page} / {totalPages}</div>
                <button className="px-3 py-1 border border-black rounded text-sm hover:bg-slate-50 disabled:opacity-50" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</button>
                <button className="px-3 py-1 border border-black rounded text-sm hover:bg-slate-50 disabled:opacity-50" onClick={() => setPage(totalPages)} disabled={page === totalPages}>Last</button>
                <select value={pageSize} onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }} className="ml-2 border border-black rounded px-2 py-1 text-sm bg-white">
                  <option value={5}>5 / page</option>
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                  <option value={50}>50 / page</option>
                </select>
              </div>
            </div>
          </div>

          {/* View Modal */}
          {viewing && (
            <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/50 p-4">
              <div className="bg-white w-full max-w-4xl rounded-lg p-6 shadow-2xl relative border border-black">
                <button className="absolute top-3 right-3 text-2xl text-slate-600 hover:text-black" onClick={() => setViewing(null)}>×</button>
                <h3 className="text-lg font-extrabold text-slate-900 mb-3">
                  Member — {viewing.member_id || viewing.final_member_id || "APPLIED"}
                </h3>
                <pre className="text-xs bg-slate-50 border border-slate-200 rounded-lg p-3 overflow-auto max-h-[55vh]">
{JSON.stringify(viewing, null, 2)}
                </pre>
                <div className="mt-4 text-right">
                  <button className="px-4 py-2 rounded border border-black bg-white text-black hover:bg-slate-50" onClick={() => setViewing(null)}>Close</button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Modal */}
          {editing && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
              <div className="bg-white rounded-lg w-full max-w-4xl p-6 relative shadow-2xl border border-black">
                <button className="absolute top-3 right-3 text-2xl text-slate-600 hover:text-black" onClick={() => setEditing(null)}>×</button>
                <h3 className="text-lg font-extrabold text-slate-900 mb-4">
                  Edit Member — {editing.member_id || editing.final_member_id || "APPLIED"}
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <TextField label="Name" value={editData.name} onChange={(v) => setEditData((x) => ({ ...x, name: v }))} />
                  <TextField label="Mobile" value={editData.mobile} onChange={(v) => setEditData((x) => ({ ...x, mobile: v }))} />
                  <TextField label="Email" value={editData.email} onChange={(v) => setEditData((x) => ({ ...x, email: v }))} />
                  <TextArea label="Address" value={editData.address} onChange={(v) => setEditData((x) => ({ ...x, address: v }))} rows={3} />
                </div>

                <div className="mt-6 flex items-center justify-end gap-2">
                  <button className="px-4 py-2 rounded border border-black bg-white text-black hover:bg-slate-50" onClick={() => setEditing(null)}>Cancel</button>
                  <button
                    className="px-4 py-2 rounded border border-black bg-black text-white hover:bg-slate-900"
                    onClick={async () => {
                      try {
                        await updateDoc(doc(db, source, editing.id), editData);
                        setEditing(null);
                      } catch (e) {
                        console.error("[edit save] error", e);
                        alert("Failed to save edits.");
                      }
                    }}
                  >
                    Save
                  </button>
                </div>
              </div>
            </div>
          )}

        </div>
      </div>
    </AdminGate>
  );
}

/* --------------- small UI bits --------------- */
function TextField({ label, value, onChange }) {
  return (
    <div>
      <label className="block text-[11px] font-bold text-slate-700 mb-1">{label}</label>
      <input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-black rounded-md px-3 py-2 text-sm focus:outline-none"
      />
    </div>
  );
}
function TextArea({ label, value, onChange, rows = 3 }) {
  return (
    <div className="md:col-span-3">
      <label className="block text-[11px] font-bold text-slate-700 mb-1">{label}</label>
      <textarea
        rows={rows}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-black rounded-md px-3 py-2 text-sm focus:outline-none"
      />
    </div>
  );
}
